import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { computeToolbarFilterPopoverLayout } from '../../board/board-search-filters/toolbarFilterPopoverLayout';
import '../../board/board-search-filters/BoardSearchFilters.css';

const PICKER_WIDTH = 238;
const PICKER_MAX_HEIGHT = 320;

export default function PermColumnFilterPopover({
  anchorRef,
  isOpen,
  onClose,
  options = [],
  selected = [],
  onApply,
  listAriaLabel = 'Opções do filtro',
  searchPlaceholder = 'Buscar',
  showSearch = true,
  popoverClassName =
    'board-search-filters-popover board-search-filters-popover--simple board-search-filters-popover--coordenador perm-column-filter-popover',
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [layout, setLayout] = useState(null);

  const items = useMemo(() => {
    const q = String(searchTerm || '').trim().toLowerCase();
    const base = (options || []).map((value) => ({ value, label: value }));
    if (!q) return base;
    return base.filter((item) => String(item.label || '').toLowerCase().includes(q));
  }, [options, searchTerm]);

  const selectableValues = useMemo(() => items.map((i) => i.value), [items]);

  const updateLayout = () => {
    const el = anchorRef?.current;
    if (!el || !isOpen) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setLayout(computeToolbarFilterPopoverLayout(rect, PICKER_WIDTH, PICKER_MAX_HEIGHT));
  };

  useLayoutEffect(() => {
    updateLayout();
  }, [isOpen, options.length, anchorRef]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return undefined;
    }
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (anchorRef?.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, onClose, anchorRef]);

  const handleToggle = (item) => {
    const next = new Set(selected || []);
    if (next.has(item.value)) next.delete(item.value);
    else next.add(item.value);
    onApply([...next]);
  };

  const handleBulkToggleAll = () => {
    if (!selectableValues.length) return;
    const allMarked = selectableValues.every((v) => (selected || []).includes(v));
    onApply(allMarked ? [] : [...selectableValues]);
  };

  if (!isOpen || !layout) return null;

  return createPortal(
    <div
      className={popoverClassName}
      style={{
        position: layout.position,
        top: layout.top,
        left: layout.left,
        width: layout.width,
        boxSizing: layout.boxSizing,
        zIndex: layout.zIndex,
        maxHeight: layout.maxHeight,
        '--bsf-cidade-popover-max-h': layout['--bsf-cidade-popover-max-h'],
        transform: layout.flipped ? 'translateY(-100%)' : undefined,
      }}
      role="dialog"
      aria-label={listAriaLabel}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        searchPlaceholder={searchPlaceholder}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showSearch={showSearch}
        items={items}
        getKey={(i) => i.value}
        getLabel={(i) => i.label}
        selectedKeys={selected || []}
        onToggle={handleToggle}
        emptyMessage="Sem valores"
        listAriaLabel={listAriaLabel}
        bulkMenuEnabled
        onBulkToggleAll={handleBulkToggleAll}
      />
    </div>,
    document.body
  );
}
